import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CALENDLY_LINK } from '../../../utils/constants';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const InteractiveToolsSection = () => {
  const [activeTab, setActiveTab] = useState('checklist');
  const [calculatorData, setCalculatorData] = useState({
    revenue: '',
    growth: '',
    margin: '',
    industry: '',
    stage: ''
  });
  const [checklistData, setChecklistData] = useState({
    category: 'financial',
    completedItems: []
  });
  const [estimatorData, setEstimatorData] = useState({
    reportType: '',
    complexity: '',
    timeline: '',
    urgency: ''
  });

  const industryOptions = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'retail', label: 'Retail & Consumer' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'realestate', label: 'Real Estate & Construction' },
    { value: 'services', label: 'Professional Services' }
  ];

  const stageOptions = [
    { value: 'startup', label: 'Startup (Pre-revenue)' },
    { value: 'early', label: 'Early Stage (0-2 years)' },
    { value: 'growth', label: 'Growth Stage (2-5 years)' },
    { value: 'mature', label: 'Mature (5+ years)' }
  ];

  const reportTypeOptions = [
    { value: 'calculation', label: 'Calculation Report' },
    { value: 'summary', label: 'Summary Report' },
    { value: 'comprehensive', label: 'Detailed Report' }
  ];

  const complexityOptions = [
    { value: 'simple', label: 'Simple (Single entity, standard industry)' },
    { value: 'moderate', label: 'Moderate (Multiple entities, some complexity)' },
    { value: 'complex', label: 'Complex (Multi-jurisdictional, unique factors)' }
  ];

  const checklistCategories = [
    {
      id: 'financial',
      name: 'Financial Information',
      items: [
        'Audited financial statements (3-5 years)',
        'Management accounts (current year)',
        'Tax returns (3-5 years)',
        'Cash flow statements',
        'Budget and forecasts',
        'Accounts receivable aging',
        'Inventory details',
        'Fixed asset register'
      ]
    },
    {
      id: 'legal',
      name: 'Legal & Corporate',
      items: [
        'Articles of incorporation',
        'Shareholder agreements',
        'Board resolutions',
        'Material contracts',
        'Intellectual property documentation',
        'Litigation records',
        'Insurance policies',
        'Regulatory compliance records'
      ]
    },
    {
      id: 'operational',
      name: 'Operational Information',
      items: [
        'Organization chart',
        'Key personnel information',
        'Customer concentration analysis',
        'Supplier agreements',
        'Market analysis reports',
        'Competitive positioning',
        'Operational metrics',
        'Strategic plans'
      ]
    }
  ];

  const calculatePreliminaryValue = () => {
    const revenue = parseFloat(calculatorData?.revenue) || 0;
    const growth = parseFloat(calculatorData?.growth) || 0;
    const margin = parseFloat(calculatorData?.margin) || 0;

    if (revenue === 0) return 0;

    // Simplified valuation logic for demonstration
    const ebitda = revenue * (margin / 100);
    let multiple = 5; // Base multiple

    // Adjust for growth
    if (growth > 20) multiple += 2;
    else if (growth > 10) multiple += 1;

    // Adjust for industry
    const industryMultipliers = {
      technology: 1.5,
      healthcare: 1.3,
      manufacturing: 1.0,
      retail: 0.8,
      realestate: 1.1,
      services: 1.2
    };

    multiple *= industryMultipliers?.[calculatorData?.industry] || 1;

    return Math.round(ebitda * multiple);
  };

  const estimateEngagementCost = () => {
    let baseCost = 5000;

    // Report type multiplier
    const reportMultipliers = {
      calculation: 0.6,
      summary: 1.0,
      comprehensive: 1.8
    };

    // Complexity multiplier
    const complexityMultipliers = {
      simple: 1.0,
      moderate: 1.5,
      complex: 2.2
    };

    baseCost *= reportMultipliers?.[estimatorData?.reportType] || 1;
    baseCost *= complexityMultipliers?.[estimatorData?.complexity] || 1;

    // Urgency premium
    if (estimatorData?.timeline === 'expedited') baseCost *= 1.3;

    return Math.round(baseCost);
  };

  const toggleChecklistItem = (item) => {
    setChecklistData(prev => ({
      ...prev,
      completedItems: prev?.completedItems?.includes(item)
        ? prev?.completedItems?.filter(i => i !== item)
        : [...prev?.completedItems, item]
    }));
  };

  const getCompletionPercentage = () => {
    const currentCategory = checklistCategories?.find(cat => cat?.id === checklistData?.category);
    if (!currentCategory) return 0;

    const completed = checklistData?.completedItems?.filter(item =>
      currentCategory?.items?.includes(item)
    )?.length;

    return Math.round((completed / currentCategory?.items?.length) * 100);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Interactive Tools
          </h2>
          <p className="text-xl text-text-secondary leading-relaxed">
            Get preliminary insights and prepare for your valuation engagement with our interactive tools designed to streamline the process.
          </p>
        </div>

        {/* Tool Tabs */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          {[
            // { id: 'calculator', name: 'Valuation Calculator', icon: 'Calculator' },
            { id: 'checklist', name: 'Data Checklist', icon: 'CheckSquare' },
            { id: 'estimator', name: 'Cost Estimator', icon: 'DollarSign' }
          ]?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 p-6 rounded-xl border-2 transition-all duration-300 ${activeTab === tab?.id
                ? 'border-accent bg-accent/5 shadow-brand'
                : 'border-border bg-white hover:border-accent/50'
                }`}
            >
              <div className="flex items-center justify-center space-x-3">
                <Icon
                  name={tab?.icon}
                  size={24}
                  className={activeTab === tab?.id ? 'text-accent' : 'text-text-secondary'}
                />
                <span className={`font-semibold ${activeTab === tab?.id ? 'text-accent' : 'text-primary'
                  }`}>
                  {tab?.name}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Tool Content */}
        <div className="bg-muted rounded-2xl p-8">
          {/* Valuation Calculator - COMMENTED OUT */}
          {/* {activeTab === 'calculator' && (
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-primary mb-4">Preliminary Valuation Calculator</h3>
                  <p className="text-text-secondary mb-6">
                    Get a rough estimate of your business value. This is for preliminary planning only and does not replace a professional valuation.
                  </p>
                </div>

                <div className="space-y-4">
                  <Input
                    label="Annual Revenue (USD)"
                    type="number"
                    placeholder="e.g., 1000000"
                    value={calculatorData?.revenue}
                    onChange={(e) => setCalculatorData(prev => ({ ...prev, revenue: e?.target?.value }))}
                  />

                  <Input
                    label="Expected Growth Rate (%)"
                    type="number"
                    placeholder="e.g., 15"
                    value={calculatorData?.growth}
                    onChange={(e) => setCalculatorData(prev => ({ ...prev, growth: e?.target?.value }))}
                  />

                  <Input
                    label="EBITDA Margin (%)"
                    type="number"
                    placeholder="e.g., 25"
                    value={calculatorData?.margin}
                    onChange={(e) => setCalculatorData(prev => ({ ...prev, margin: e?.target?.value }))}
                  />

                  <Select
                    label="Industry"
                    options={industryOptions}
                    value={calculatorData?.industry}
                    onChange={(value) => setCalculatorData(prev => ({ ...prev, industry: value }))}
                    placeholder="Select your industry"
                  />

                  <Select
                    label="Business Stage"
                    options={stageOptions}
                    value={calculatorData?.stage}
                    onChange={(value) => setCalculatorData(prev => ({ ...prev, stage: value }))}
                    placeholder="Select business stage"
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h4 className="text-xl font-semibold text-primary mb-6">Preliminary Estimate</h4>
                
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-accent mb-2">
                    ${calculatePreliminaryValue()?.toLocaleString()}
                  </div>
                  <p className="text-text-secondary">Estimated Business Value</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-text-secondary">Revenue Multiple</span>
                    <span className="font-medium text-primary">
                      {calculatorData?.revenue ? (calculatePreliminaryValue() / parseFloat(calculatorData?.revenue))?.toFixed(1) : '0.0'}x
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-text-secondary">EBITDA Multiple</span>
                    <span className="font-medium text-primary">
                      {calculatorData?.revenue && calculatorData?.margin ? 
                        (calculatePreliminaryValue() / (parseFloat(calculatorData?.revenue) * parseFloat(calculatorData?.margin) / 100))?.toFixed(1) : '0.0'}x
                    </span>
                  </div>
                </div>

                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-warning mb-1">Disclaimer</p>
                      <p className="text-xs text-text-secondary">
                        This is a preliminary estimate only. Actual valuations require detailed analysis and professional judgment.
                      </p>
                    </div>
                  </div>
                </div>

                <Button variant="default" fullWidth iconName="Calendar" iconPosition="left">
                  Schedule Professional Valuation
                </Button>
              </div>
            </div>
          )} */}

          {/* Data Checklist */}
          {activeTab === 'checklist' && (
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-4">Data Preparation Checklist</h3>
                <p className="text-text-secondary mb-6">
                  Ensure you have all necessary documents ready for your valuation engagement. Complete preparation accelerates the process.
                </p>

                <div className="flex space-x-2 mb-6">
                  {checklistCategories?.map((category) => (
                    <button
                      key={category?.id}
                      onClick={() => setChecklistData(prev => ({ ...prev, category: category?.id }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${checklistData?.category === category?.id
                        ? 'bg-accent text-white' : 'bg-white text-text-primary hover:bg-accent/10'
                        }`}
                    >
                      {category?.name}
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-xl p-6">
                  {checklistCategories?.map((category) => (
                    checklistData?.category === category?.id && (
                      <div key={category?.id}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-primary">{category?.name}</h4>
                          <div className="text-sm text-accent font-medium">
                            {getCompletionPercentage()}% Complete
                          </div>
                        </div>

                        <div className="w-full bg-border rounded-full h-2 mb-6">
                          <div
                            className="bg-accent h-2 rounded-full transition-all duration-300"
                            style={{ width: `${getCompletionPercentage()}%` }}
                          ></div>
                        </div>

                        <ul className="space-y-3">
                          {category?.items?.map((item, idx) => (
                            <li key={idx} className="flex items-center space-x-3">
                              <button
                                onClick={() => toggleChecklistItem(item)}
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${checklistData?.completedItems?.includes(item)
                                  ? 'bg-success border-success' : 'border-border hover:border-accent'
                                  }`}
                              >
                                {checklistData?.completedItems?.includes(item) && (
                                  <Icon name="Check" size={12} className="text-white" />
                                )}
                              </button>
                              <span className={`text-sm ${checklistData?.completedItems?.includes(item)
                                ? 'text-text-secondary line-through' : 'text-text-primary'
                                }`}>
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h4 className="text-xl font-semibold text-primary mb-6">Preparation Tips</h4>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={16} className="text-accent" />
                    </div>
                    <div>
                      <h5 className="font-medium text-primary mb-2">Organize Documents</h5>
                      <p className="text-sm text-text-secondary">
                        Keep all documents in digital format, clearly labeled and organized by category for easy access.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Clock" size={16} className="text-accent" />
                    </div>
                    <div>
                      <h5 className="font-medium text-primary mb-2">Start Early</h5>
                      <p className="text-sm text-text-secondary">
                        Begin gathering documents as soon as you decide to proceed. This prevents delays in the valuation process.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Users" size={16} className="text-accent" />
                    </div>
                    <div>
                      <h5 className="font-medium text-primary mb-2">Involve Key Personnel</h5>
                      <p className="text-sm text-text-secondary">
                        Ensure your CFO, accountant, and legal counsel are aware and can assist with document preparation.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Shield" size={16} className="text-accent" />
                    </div>
                    <div>
                      <h5 className="font-medium text-primary mb-2">Confidentiality</h5>
                      <p className="text-sm text-text-secondary">
                        All documents will be handled with strict confidentiality. We'll provide a secure portal for document sharing.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-2 text-sm text-text-secondary border-t border-border">
                  {/* <Button variant="outline" fullWidth iconName="Download" iconPosition="left">
                    Download Complete Checklist
                  </Button> */}

                  <i>Disclaimer: This checklist is illustrative and not comprehensive. Required information may vary by engagement type, industry, and case specifics. Additional supporting documentation may be requested as needed.</i>
                </div>
              </div>
            </div>
          )}

          {/* Cost Estimator */}
          {activeTab === 'estimator' && (
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-primary mb-4">Engagement Cost Estimator</h3>
                  <p className="text-text-secondary mb-6">
                    Get an estimate of your valuation engagement cost based on report type, complexity, and timeline requirements.
                  </p>
                </div>

                <div className="space-y-4">
                  <Select
                    label="Report Type"
                    options={reportTypeOptions}
                    value={estimatorData?.reportType}
                    onChange={(value) => setEstimatorData(prev => ({ ...prev, reportType: value }))}
                    placeholder="Select report type"
                  />

                  <Select
                    label="Engagement Complexity"
                    options={complexityOptions}
                    value={estimatorData?.complexity}
                    onChange={(value) => setEstimatorData(prev => ({ ...prev, complexity: value }))}
                    placeholder="Select complexity level"
                  />

                  <Select
                    label="Timeline Requirements"
                    options={[
                      { value: 'standard', label: 'Standard (3-4 weeks)' },
                      { value: 'expedited', label: 'Expedited (2-3 weeks) - Premium applies' }
                    ]}
                    value={estimatorData?.timeline}
                    onChange={(value) => setEstimatorData(prev => ({ ...prev, timeline: value }))}
                    placeholder="Select timeline"
                  />

                  <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-accent mb-1">Estimate Only</p>
                        <p className="text-xs text-text-secondary">
                          Final pricing depends on specific engagement requirements and will be confirmed during consultation.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h4 className="text-xl font-semibold text-primary mb-6">Cost Estimate</h4>

                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-accent mb-2">
                    ${estimateEngagementCost()?.toLocaleString()}
                  </div>
                  <p className="text-text-secondary">Estimated Investment</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-text-secondary">Base Cost</span>
                    <span className="font-medium text-primary">$5,000</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-text-secondary">Report Type Adjustment</span>
                    <span className="font-medium text-primary">
                      {estimatorData?.reportType === 'calculation' ? '-40%' :
                        estimatorData?.reportType === 'summary' ? '0%' :
                          estimatorData?.reportType === 'comprehensive' ? '+80%' : '0%'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-text-secondary">Complexity Adjustment</span>
                    <span className="font-medium text-primary">
                      {estimatorData?.complexity === 'simple' ? '0%' :
                        estimatorData?.complexity === 'moderate' ? '+50%' :
                          estimatorData?.complexity === 'complex' ? '+120%' : '0%'}
                    </span>
                  </div>
                  {estimatorData?.timeline === 'expedited' && (
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-text-secondary">
                        Expedited Premium
                      </span>
                      <span className="font-medium text-warning">
                        +30%
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-4 mb-6">
                  <h5 className="font-semibold text-primary">What's Included:</h5>
                  <ul className="text-sm text-text-secondary space-y-2">
                    <li className="flex items-center space-x-2">
                      <Icon name="Check" size={12} className="text-success" />
                      <span>Professional valuation report</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Icon name="Check" size={12} className="text-success" />
                      <span>Management presentation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Icon name="Check" size={12} className="text-success" />
                      <span>Unlimited revisions (within scope)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Icon name="Check" size={12} className="text-success" />
                      <span>Post-delivery support (30 days)</span>
                    </li>
                  </ul>
                </div>

                <Button variant="default" fullWidth iconName="MessageSquare" iconPosition="left" asChild>
                  <Link to="/book-consultation">
                    Discuss Your Requirements
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InteractiveToolsSection;